import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface UserCredit {
  user_id: string;
  monthly_credits: number;
  purchased_credits: number;
  is_trial_active: boolean;
  trial_end_date: string | null;
}

const SubscriptionManagement = () => {
  const [credits, setCredits] = useState<UserCredit[]>([]);
  const [profiles, setProfiles] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch user credits
      const { data: creditsData, error: creditsError } = await supabase
        .from('user_credits')
        .select('user_id, monthly_credits, purchased_credits, is_trial_active, trial_end_date')
        .order('monthly_credits', { ascending: false });

      if (creditsError) throw creditsError;

      // Fetch profiles for display names
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name');

      if (profilesError) throw profilesError;

      const profilesMap = new Map(
        profilesData?.map(p => [p.user_id, p.display_name || 'Unnamed User']) || []
      );

      setCredits(creditsData || []);
      setProfiles(profilesMap);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription Management</CardTitle>
        <CardDescription>
          {credits.length} users with credit accounts
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {credits.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No subscription data found
            </p>
          ) : (
            credits.map((credit) => (
              <div
                key={credit.user_id}
                className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="space-y-2">
                  <div className="font-medium">
                    {profiles.get(credit.user_id)}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="default">
                      {credit.monthly_credits} monthly credits
                    </Badge>
                    {credit.purchased_credits > 0 && (
                      <Badge variant="secondary">
                        {credit.purchased_credits} purchased
                      </Badge>
                    )}
                    {credit.is_trial_active && (
                      <Badge variant="outline">
                        Trial Active
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground mt-2 md:mt-0">
                  {credit.is_trial_active && credit.trial_end_date && (
                    <div>
                      Trial ends {format(new Date(credit.trial_end_date), 'PP')}
                    </div>
                  )}
                  <div className="font-semibold">
                    Total: {credit.monthly_credits + credit.purchased_credits} credits
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionManagement;
